import onsiteproLogo from "../assets/logo2 2.png";

export const PRIMARY_FORM = [139, 22, 52];

export function drawRoundedRect(doc, x, y, width, height, radius, fillColor, strokeColor) {
  doc.setFillColor(...fillColor);
  doc.setDrawColor(...strokeColor);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, width, height, radius, radius, "FD");
}

export async function addLogoAndFormHeader(doc, title) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoWidth = 70;
  const logoX = 10;
  const titleStartX = logoX + logoWidth + 8;
  const titleMaxWidth = pageWidth - titleStartX - 10;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  const titleLines = doc.splitTextToSize(String(title || ""), titleMaxWidth);
  const lineHeight = 5.2;
  const subtitleGap = 4.5;
  const headerPadding = 8;
  const textBlockHeight = titleLines.length * lineHeight + subtitleGap + 4;
  const headerHeight = Math.max(30, headerPadding * 2 + textBlockHeight);

  drawRoundedRect(doc, 0, 0, pageWidth, headerHeight, 0, PRIMARY_FORM, PRIMARY_FORM);
  await new Promise((resolve) => {
    const logoImg = new Image();
    logoImg.crossOrigin = "anonymous";
    logoImg.onload = function () {
      try {
        const aspectRatio = logoImg.height / logoImg.width;
        const logoHeight = logoWidth * aspectRatio;
        const logoY = (headerHeight - logoHeight) / 2;
        const canvas = document.createElement("canvas");
        const scale = 2.5;
        canvas.width = logoImg.width * scale;
        canvas.height = logoImg.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);
        ctx.drawImage(logoImg, 0, 0);
        doc.addImage(canvas.toDataURL("image/png", 1.0), "PNG", logoX, logoY, logoWidth, logoHeight, "", "NONE");
      } catch {
        doc.setFillColor(255, 255, 255);
        doc.rect(logoX, headerHeight / 2 - 7.5, 60, 15, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text("TAYLOR TECHNOLOGY", logoX + 5, headerHeight / 2 - 2);
        doc.text("SYSTEMS LIMITED", logoX + 5, headerHeight / 2 + 3);
      }
      resolve();
    };
    logoImg.onerror = function () {
      doc.setFillColor(255, 255, 255);
      doc.rect(logoX, headerHeight / 2 - 7.5, 60, 15, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text("TAYLOR TECHNOLOGY", logoX + 5, headerHeight / 2 - 2);
      doc.text("SYSTEMS LIMITED", logoX + 5, headerHeight / 2 + 3);
      resolve();
    };
    logoImg.src = onsiteproLogo;
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  const titleStartY = headerPadding + 4;
  titleLines.forEach((line, i) => {
    doc.text(line, titleStartX, titleStartY + i * lineHeight);
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleStartX, titleStartY + titleLines.length * lineHeight + subtitleGap);
  return headerHeight + 10;
}

export function applyFooterAllPages(doc, options = {}) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const totalPages = doc.getNumberOfPages();
  const showGeneratedOn = options.showGeneratedOn === true;
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const footerY = pageHeight - 25;
    drawRoundedRect(doc, 0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    if (showGeneratedOn) {
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
        20,
        footerY + 8
      );
    }
    doc.text(`Page ${p} of ${totalPages}`, pageWidth - 35, footerY + 15);
  }
}

export function getInnerTableWidth(doc) {
  return doc.internal.pageSize.getWidth() - 20;
}

// Strips the characters SharePoint/Windows reject in a file name. Note that "/"
// goes too, so a dd/mm/yyyy date comes out as dd-mm-yyyy.
export function sanitizeFileNamePart(value) {
  return String(value || "").replace(/[\\/:"*?<>|]+/g, "-").trim();
}

const DMY = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/;
const YMD = /^(\d{4})-(\d{2})-(\d{2})/;

// Forms store dates every which way — "28/07/2026", "2026-07-28", ISO strings from
// Date-typed fields, and free text. SharePoint names use one format so a folder
// sorted by name stays readable, so normalise everything to dd-mm-yyyy. Anything
// unparseable is passed through rather than dropped — a wrong-looking date in the
// file name is still better than no date.
export function toDdMmYyyy(value) {
  if (!value) return "";
  const raw = String(value).trim();

  const dmy = raw.match(DMY);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
  }

  const ymd = raw.match(YMD);
  if (ymd) {
    const [, y, m, d] = ymd;
    return `${d}-${m}-${y}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const d = String(parsed.getDate()).padStart(2, "0");
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    return `${d}-${m}-${parsed.getFullYear()}`;
  }

  return raw;
}

// The admin who filed the form. Used as the name segment on the forms that have no
// person of their own — it matches the dashboard's "Added By" column.
export function addedByName(record) {
  return [record?.addedByFirstName, record?.addedByLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
}

// Client-requested SharePoint naming: "PERSON - SITE - DATE ISSUED.pdf".
// Blank parts are dropped rather than left as an empty " -  - " gap.
export function buildSharePointFileName(parts) {
  const name = parts.map(sanitizeFileNamePart).filter(Boolean).join(" - ");
  return `${name || "Form"}.pdf`;
}
